import React, { ReactNode, useEffect, useMemo, useState } from 'react';
import classNames from 'classnames';
import { Badge, Button, Navbar } from 'vetrf-ui';
import { BadgeColor } from 'vetrf-ui/dist/components/other/Badge/Badge';
import { useUserDataContext } from 'components/UserDataContext/context';
import { Sidebar } from 'components/Sidebar';
import { useGetPermission } from 'utils/hooks/useGetPermission';
import { ROLES } from 'utils/consts/roles';
import { EnvironmentType } from 'components/UserDataContext/types';
import { WorkParamsDialog } from './WorkParamsDialog';
import { SCROLL_OFFSET, SIDEBAR_FLOAT_WIDTH } from './consts';
import mainLogo from './irena.png';
import userLogo from './user.jpg';
import styles from './BaseWrapper.module.scss';

type BaseWrapperProps = {
  children: ReactNode;
};

export const BaseWrapper = ({ children }: BaseWrapperProps) => {
  const { userData, initInfo, isUserDataLoading } = useUserDataContext();
  const [isSidebarShowing, setIsSidebarShowing] = useState(
    window.innerWidth >= SIDEBAR_FLOAT_WIDTH
  );
  const [isScrollButtonVisible, setIsScrollButtonVisible] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const adminPermission = useGetPermission(ROLES.admin, userData);
  const adminBePermission = useGetPermission(ROLES.adminBe, userData);

  const handleToggleModal = () => {
    setIsModalOpen((prev) => !prev);
  };

  const onNavbarToggleClick = () => {
    setIsSidebarShowing((prev) => !prev);
  };

  const closeFloatingSidebar = () => {
    if (window.innerWidth < SIDEBAR_FLOAT_WIDTH) {
      setIsSidebarShowing(false);
    }
  };

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  };

  const onResize = () => setIsSidebarShowing(window.innerWidth >= SIDEBAR_FLOAT_WIDTH);
  const toggleScrollButtonVisibility = () =>
    setIsScrollButtonVisible(window.pageYOffset > SCROLL_OFFSET);

  useEffect(() => {
    window.addEventListener('resize', onResize);
    window.addEventListener('scroll', toggleScrollButtonVisibility);
    return () => {
      window.removeEventListener('resize', onResize);
      window.removeEventListener('scroll', toggleScrollButtonVisibility);
    };
  }, []);

  const userInfo = useMemo(() => {
    if (!userData) {
      return undefined;
    }
    const { fio, login, currentBusinessEntity, currentSupervisedObject } = userData;
    return {
      fio,
      login,
      institution: currentBusinessEntity && {
        shortName: `${currentBusinessEntity?.businessEntity.firmNameShort || ''}
        ${currentSupervisedObject?.supervisedObject.name || ''}`,
      },
    };
  }, [userData]);

  const navbarItems = useMemo(() => {
    const navbarArray = [];
    if (adminPermission || adminBePermission) {
      navbarArray.push({
        text: 'Администрирование',
        onClick: () => {
          document.location.assign('/#/ac');
        },
      });
    }
    if (
      (userData?.patentHolders && userData.patentHolders.length > 1) ||
      (userData?.supervisedObjects && userData.supervisedObjects.length > 1)
    ) {
      navbarArray.push({
        text: 'Параметры работы в системе',
        onClick: handleToggleModal,
      });
    }
    return navbarArray;
  }, [userData, adminPermission, adminBePermission]);

  const environmentBadge = useMemo<{ text: string; color?: BadgeColor }>(() => {
    switch (initInfo?.environmentType) {
      case EnvironmentType.DEMO:
        return {
          text: 'ТЕСТИРОВАНИЕ',
          color: 'danger',
        };

      case EnvironmentType.DEV_FORS:
        return {
          text: initInfo.environmentType.split('_')[0],
          color: 'pink',
        };
      case EnvironmentType.TEST_FORS:
      case EnvironmentType.TEST2_FORS:
        return {
          text: initInfo.environmentType.split('_')[0],
          color: 'inverse',
        };
      case EnvironmentType.HOTFIX:
        return {
          text: initInfo.environmentType,
          color: 'warning',
        };
      default:
        return { text: '' };
    }
  }, [initInfo]);

  useEffect(() => {
    if (
      !isUserDataLoading &&
      window.location.pathname.includes('react') &&
      userData?.id &&
      userData.supervisedObjectClarification
    ) {
      setIsModalOpen(true);
    }
  }, [userData, isUserDataLoading]);

  return (
    <div className={classNames(userInfo?.institution && 'nav-institution')}>
      <Navbar
        avatarSrc={userLogo}
        logo={{
          image: {
            source: mainLogo,
          },
          text: 'Гален',
        }}
        user={userInfo}
        items={navbarItems}
        loginBtn={{
          href: `/login.html?relayState=${encodeURIComponent(window.location.href)}`,
        }}
      />

      {!!environmentBadge.text && (
        <Badge color={environmentBadge.color} className={styles.environmentBadge}>
          {environmentBadge.text}
        </Badge>
      )}

      <button type="button" onClick={onNavbarToggleClick} className={styles.navbarToggle}>
        ☰
      </button>

      {isSidebarShowing ? <Sidebar onLinkClick={closeFloatingSidebar} /> : ''}

      <div className={`mainContent ${styles.mainContent}`}>
        <div className={styles.content}>{children}</div>
        <footer>
          <div className={styles.footerInner}>
            <p className={styles.mb2}>
              Гален {initInfo?.version} © 2007 – {new Date().getFullYear()} Федеральная служба
              ветеринарного и фитосанитарного надзора (Россельхознадзор)
            </p>
            <p>
              По вопросам технической поддержки обращайтесь по адресу{' '}
              <a href="mailto:galen@fsvps.ru">galen@fsvps.ru</a>
            </p>
          </div>
        </footer>
      </div>

      <Button
        className={classNames(
          'btn btn-sm btn-inverse',
          styles.buttonScroll,
          isScrollButtonVisible && styles.btnVisible
        )}
        icon="angle-double-up m-0"
        iconColor="white"
        iconSize={200}
        onClick={scrollToTop}
        title="Вверх"
      />

      {isModalOpen && <WorkParamsDialog onClose={handleToggleModal} />}
    </div>
  );
};
